import { ProductContext } from 'apps/widget/src/MainContext';
import { ConfigContext } from 'apps/widget/src/WidgetEventsManager';
import { NoResultsPlaceholder } from 'apps/widget/src/components/NoResultsPlaceholder';
import { ProductDetails } from 'apps/widget/src/components/ProductDetails';
import { StaticQuizEndScreen } from 'apps/widget/src/components/StaticQuizEndScreen';
import { WidgetNavigationController } from 'apps/widget/src/components/WidgetNavigationController';
import { AvailableMatchingType } from 'apps/widget/src/model/matchingType';
import clsx from 'clsx';
import { useCallback, useContext, useMemo } from 'react';

import { MIME_TYPE_IMAGE_JPEG, MIME_TYPE_VIDEO_MP4, isVideo } from '@vestico/utils';

import { WidgetItemAspectRatio } from '@vestico/model';

import { GlobalsContext, MediaDisplay, Spinner, SpinnerPositioning, SpinnerSize, SplitScreen } from '@vestico/shared';

import { ReviewDetails } from '../../../components/ReviewDetails';
import { SizeRecommendationDetails } from '../../matching/SizeRecommendationDetails';
import { SizeRecommendationTabSelect } from '../../matching/SizeRecommendationTabSelect';
import { CarouselContentProps } from './CarouselContent';
import style from './CarouselDesktop.module.css';

export interface CarouselDesktopProps {
  showBodyInfoForm: boolean;
}

export const CarouselDesktop = ({
  showBodyInfoForm,
  responseIndex,
  responses,
  products,
  onProductLinkClick,
  onBeginUserMatchInfo,
  showBeginUserMatchInfoSection,
  anyResponseHasMatch,
  activeMatchingType,
  hasSizeRecAvailable,
  hasContentMatchAvailable,
  sizeRecommendation,
  onActiveMatchingTypeChange,
}: CarouselContentProps & CarouselDesktopProps) => {
  const { stylesConfig } = useContext(GlobalsContext);
  const { sizeChart: productSizeChart } = useContext(ProductContext);

  const { isQuiz: isQuizVar } = useContext(ConfigContext);
  const isQuiz = useMemo(() => isQuizVar && !responses, [isQuizVar, responses]);

  const sizeChart = useMemo(() => sizeRecommendation?.chart ?? productSizeChart, [sizeRecommendation, productSizeChart]);

  const singleResponseProduct = useMemo(() => (products ? products[0] : products), [products]);

  const response = useMemo(() => (responses && responseIndex !== undefined ? responses[responseIndex] : undefined), [responseIndex, responses]);
  const isSingleProductResponse = useMemo(() => response && response.products.length === 1, [response]);

  const aspectRatio: WidgetItemAspectRatio = useMemo(() => '3:4', []);
  const cropHint = useMemo(
    () => response?.media?.cropHints && aspectRatio && response?.media.cropHints[aspectRatio],
    [response?.media.cropHints, aspectRatio]
  );

  const showSizeRecRight = activeMatchingType === 'size-rec';

  const onActiveMatchingTypeToggle = useCallback(
    (type: AvailableMatchingType) => () => onActiveMatchingTypeChange(type),
    [onActiveMatchingTypeChange]
  );

  const { likesEnabled } = stylesConfig.stylesConfig;
  const hasProducts = useMemo(() => products && products?.length > 0, [products]);

  const right = useMemo(() => {
    if (showBodyInfoForm) {
      return <WidgetNavigationController category={singleResponseProduct?.matchingCategory} />;
    }

    if (showSizeRecRight) {
      if (isQuiz && !response && !sizeRecommendation && !hasProducts) {
        return <StaticQuizEndScreen />;
      }
      const isUnmatchedSizeChart = sizeChart?.unmatched;
      if (showBodyInfoForm || isUnmatchedSizeChart) {
        return (
          <SizeRecommendationTabSelect
            sizeChart={sizeChart}
            sizeRecTabContent={!isUnmatchedSizeChart ? <WidgetNavigationController category={singleResponseProduct?.matchingCategory} /> : undefined}
            showSizeRecTab={!isUnmatchedSizeChart}
          />
        );
      }

      return (
        <SizeRecommendationDetails
          sizeRecommendation={sizeRecommendation}
          sizeChart={sizeChart}
          showContentToggle={hasContentMatchAvailable}
          onContentToggle={onActiveMatchingTypeToggle('content')}
        />
      );
    }

    if (isQuiz && !response) {
      return <StaticQuizEndScreen />;
    }

    if (!response) {
      return null;
    }

    return (
      <ReviewDetails
        response={response}
        products={products}
        scrollEnabled
        showLikeInteraction={likesEnabled}
        showProducts
        showSizeRecToggle={hasSizeRecAvailable && isSingleProductResponse}
        onSizeRecToggle={onActiveMatchingTypeToggle('size-rec')}
        onProductLinkClick={onProductLinkClick}
        onBeginUserMatchInfo={onBeginUserMatchInfo}
        showBeginUserMatchInfoSection={showBeginUserMatchInfoSection}
        anyResponseHasMatch={anyResponseHasMatch}
      />
    );
  }, [
    showSizeRecRight,
    showBodyInfoForm,
    isQuiz,
    response,
    products,
    likesEnabled,
    hasSizeRecAvailable,
    isSingleProductResponse,
    onActiveMatchingTypeToggle,
    onProductLinkClick,
    onBeginUserMatchInfo,
    showBeginUserMatchInfoSection,
    anyResponseHasMatch,
    sizeChart,
    sizeRecommendation,
    hasContentMatchAvailable,
    singleResponseProduct?.matchingCategory,
    hasProducts,
  ]);

  const id = useMemo(() => (!showBodyInfoForm ? `bd-${responseIndex}` : 'enterinfo'), [responseIndex, showBodyInfoForm]);
  const splitRatio = useMemo(() => {
    if (isQuiz && !hasProducts) {
      return 0.0;
    }
    return !showBodyInfoForm && !showSizeRecRight ? undefined : 0.32;
  }, [showBodyInfoForm, showSizeRecRight, isQuiz, hasProducts]);

  const left = useMemo(() => {
    if (showBodyInfoForm || showSizeRecRight) {
      return (
        <div className={clsx(style.leftPartEnterMatchInfo)}>
          <ProductDetails product={singleResponseProduct} />
        </div>
      );
    }

    if (isQuiz && !response) {
      return <div />;
    }

    if (!response) {
      return null;
    }

    const { mainMimeType, imageSources, videoSources } = response.media;
    const jpegSources = imageSources[MIME_TYPE_IMAGE_JPEG];

    return (
      <MediaDisplay
        thumbURL={jpegSources?.[180]}
        imageURL={jpegSources[1200]}
        videoURL={videoSources?.[MIME_TYPE_VIDEO_MP4]?.[1920]}
        srcSet={imageSources}
        mimeType={mainMimeType}
        playVideo
        borderRadius={['var(--border-radius)', '0px', '0px', 'var(--border-radius)']}
        animate
        mediaFit={isVideo(mainMimeType) ? 'contain' : undefined}
        cropHint={cropHint}
        alwaysShowVideo
      />
    );
  }, [showBodyInfoForm, showSizeRecRight, response, isQuiz, cropHint, singleResponseProduct]);

  const content = useMemo(() => {
    if (left && right) {
      return <SplitScreen left={left} right={right} id={id} splitRatio={splitRatio} />;
    }
    if (responses === null || responses?.length === 0) {
      return <NoResultsPlaceholder />;
    }
    return <Spinner positioning={SpinnerPositioning.default} size={SpinnerSize.medium} />;
  }, [left, right, responses, id, splitRatio]);

  return <div className={clsx(style.root)}>{content}</div>;
};
